import { useCallback, useEffect, useRef, useState } from "react";
import AwesomeDebouncePromise from "awesome-debounce-promise";
import { ContentBlock, convertToRaw, DraftHandleValue, Editor, EditorState, Modifier, RichUtils } from "draft-js";
import { deferExecution } from "@Utils";

/**
 * Props for the useRichTextEditor hook.
 */
interface IProps {
    /**
     * Callback function triggered when content changes.
     */
    onChange?: (content: string) => void;

    /**
     * Initial content for the editor.
     */
    initialContent?: EditorState;
}

/**
 * A custom hook for managing rich text editor state and operations.
 */
const useRichTextEditorPresenter = ({ onChange, initialContent }: IProps = {}) => {
    const [editorState, setEditorState] = useState(() => initialContent ?? EditorState.createEmpty());
    const editor = useRef<Editor>(null);

    const isSet = useRef(false);

    /**
     * Effect to handle updates to initialContent prop.
     */
    useEffect(() => {
        if (!isSet.current) {
            setEditorState((initialContent) => initialContent);
            isSet.current = true;
        }
    }, [initialContent]);

    const lastContentState = useRef(editorState.getCurrentContent());

    const debouncedOnChange = useRef(
        AwesomeDebouncePromise((content: string) => {
            onChange?.(content);
        }, 2000)
    );

    /**
     * Focuses the editor when clicked.
     */
    const focus = () => {
        if (editor.current) {
            editor.current.focus();
        }
    };

    /**
     * Handles changes to the editor state.
     * @param newEditorState - The new editor state.
     */
    const handleChange = useCallback(
        (newEditorState: EditorState) => {
            const newContentState = newEditorState.getCurrentContent();

            if (newContentState !== lastContentState.current) {
                lastContentState.current = newContentState;
                if (onChange) {
                    const content = JSON.stringify(convertToRaw(newContentState));
                    debouncedOnChange.current(content);
                }
            }

            setEditorState(newEditorState);
        },
        [onChange]
    );

    /**
     * Handles key commands in the editor.
     * @param command - The key command.
     * @param editorState - The current editor state.
     */
    const handleKeyCommand = useCallback(
        (command: string, editorState: EditorState): DraftHandleValue => {
            const newState = RichUtils.handleKeyCommand(editorState, command);
            if (newState) {
                handleChange(newState);
                return "handled";
            }
            return "not-handled";
        },
        [handleChange]
    );

    /**
     * Toggles a block type in the editor.
     * @param blockType - The block type to toggle.
     */
    const toggleBlockType = useCallback(
        async (blockType: string) => {
            const newState = RichUtils.toggleBlockType(editorState, blockType);
            if (newState !== editorState) {
                handleChange(newState);
            }
            await deferExecution(focus);
        },
        [editorState, handleChange]
    );

    /**
     * Toggles an inline style in the editor.
     * @param inlineStyle - The inline style to toggle.
     */
    const toggleInlineStyle = useCallback(
        (inlineStyle: string) => {
            const newState = RichUtils.toggleInlineStyle(editorState, inlineStyle);

            if (newState !== editorState) {
                handleChange(newState);
            }
        },
        [editorState, handleChange]
    );

    /**
     * Toggles text alignment in the editor.
     * @param alignmentStyle - The alignment style to toggle.
     */
    const toggleAlignment = useCallback(
        async (alignmentStyle: string) => {
            const selection = editorState.getSelection();
            const currentContent = editorState.getCurrentContent();
            const currentBlock = currentContent.getBlockForKey(selection.getStartKey());
            const blockData = currentBlock.getData().set("textAlign", alignmentStyle);

            const newContentState = Modifier.setBlockData(currentContent, selection, blockData);
            const newEditorState = EditorState.push(editorState, newContentState, "change-block-data");

            handleChange(newEditorState);
        },
        [editorState, handleChange]
    );

    const classNames = ["rich-text-editor-editor"];
    const contentState = editorState.getCurrentContent();

    if (!contentState.hasText()) {
        if (contentState.getBlockMap().first().getType() !== "unstyled") {
            classNames.push("rich-text-editor-hide-placeholder");
        }
    }

    /**
     * Generates the style for a content block.
     * @param contentBlock - The content block to style.
     */
    const blockStyleFn = (contentBlock: ContentBlock): string => {
        const type = contentBlock.getType();
        const data = contentBlock.getData();
        const alignment = data.get("textAlign") as string;

        const classes = [type];
        if (typeof alignment === "string") {
            classes.push(`text-align-${alignment}`);
        }
        return classes.join(" ");
    };

    return {
        editorState,

        handlers: {
            handleChange,
            handleKeyCommand,
            toggleAlignment,
            toggleBlockType,
            toggleInlineStyle,
            focus,
        },

        uiModel: {
            className: classNames.join(" "),
            blockStyleFn,
            editor,
        },
    };
};

export default useRichTextEditorPresenter;